import { useQuery } from "@tanstack/react-query";

/**
 * Tracks whether a LayerZero message has been delivered on the destination
 * chain.
 *
 * A source transaction confirming only means the message was accepted for
 * relay — the funds arrive a minute or two later. Without this the success
 * screen can only say "initiated" and hand the user a link to go and check
 * for themselves.
 *
 * The scan API 404s (code 4040) until the message is indexed, which is the
 * normal state for the first several seconds after a send. That is treated as
 * "still pending", not as an error.
 */

const SCAN_API = "https://scan.layerzero-api.com/v1/messages/tx";

export type LayerZeroDelivery =
  | { kind: "pending" }
  | { kind: "delivered"; destinationTxHash?: string }
  | { kind: "failed"; reason: string };

/** Statuses the scan API reports once a message has stopped moving. */
const FAILED_STATUSES = new Set(["FAILED", "BLOCKED", "PAYLOAD_STORED"]);

type ScanMessage = {
  status?: { name?: string; message?: string };
  destination?: { status?: string; tx?: { txHash?: string } };
};

const readStatus = (message: ScanMessage | undefined): LayerZeroDelivery => {
  const name = message?.status?.name?.toUpperCase();
  if (!name) return { kind: "pending" };

  if (name === "DELIVERED") {
    return {
      kind: "delivered",
      destinationTxHash: message?.destination?.tx?.txHash,
    };
  }

  if (FAILED_STATUSES.has(name)) {
    return {
      kind: "failed",
      reason:
        message?.status?.message ??
        "The message could not be delivered on the destination chain.",
    };
  }

  // INFLIGHT, CONFIRMING and anything the API adds later — still moving.
  return { kind: "pending" };
};

const useLayerZeroDelivery = ({
  txHash,
  enabled = true,
}: {
  /** The SOURCE chain transaction hash of the bridge send. */
  txHash?: string;
  enabled?: boolean;
}): LayerZeroDelivery => {
  const { data } = useQuery<LayerZeroDelivery>({
    queryKey: ["layerzero-delivery", txHash],
    enabled: Boolean(txHash) && enabled,
    // Stop polling once the message reaches a terminal state.
    refetchInterval: (query) =>
      query.state.data?.kind === "pending" || query.state.data === undefined
        ? 5_000
        : false,
    queryFn: async () => {
      const res = await fetch(`${SCAN_API}/${txHash}`);

      // Not indexed yet — expected immediately after the send.
      if (res.status === 404) return { kind: "pending" } as const;
      if (!res.ok) return { kind: "pending" } as const;

      const body = (await res.json()) as { data?: ScanMessage[] };
      return readStatus(body?.data?.[0]);
    },
  });

  return data ?? { kind: "pending" };
};

export default useLayerZeroDelivery;
